import { ChartTooltip } from '@/components/analytics/ChartTooltip.tsx'
import {
  CHART_CURSOR_STROKE_COLOR,
  CHART_GRID_BORDER_COLOR,
  CHART_X_AXIS_TICK_LABEL_COLOR,
  CHART_Y_AXIS_TICK_LABEL_COLOR,
} from '@/constants/ANALYTICS.ts'
import type { StackedBarChartConfig, StackedBarData } from '@/types/chart'
import {
  Bar,
  CartesianGrid,
  BarChart as RechartsBarChart,
  Rectangle,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'

interface StackedBarChartProps {
  data: StackedBarData[]
  config: StackedBarChartConfig
  height: number
}

export const StackedBarChart = ({ data, config, height }: StackedBarChartProps) => {
  const chartData = data.map((stackedBarData, index, array) => ({
    ...stackedBarData,
    xValue: config.xFormatter(
      stackedBarData.iterationDate,
      index,
      array.map((d) => d.iterationDate),
    ),
  }))

  if (chartData.length === 0) return

  // biome-ignore lint/suspicious/noExplicitAny: <explanation>
  function CustomCursor({ x, y, height, width }: any) {
    const widthOverride = 0.5
    return (
      <Rectangle stroke={CHART_CURSOR_STROKE_COLOR} x={x + width / 2} y={y} width={widthOverride} height={height} />
    )
  }

  // @ts-ignore
  return (
    <ResponsiveContainer>
      <RechartsBarChart
        className="grid-last-line-only"
        data={chartData}
        margin={{ top: 8, right: 10, bottom: 0, left: 10 }}
      >
        <CartesianGrid horizontal={true} vertical={true} stroke={CHART_GRID_BORDER_COLOR} />
        <ReferenceLine
          x={chartData[chartData.length - 1].xValue}
          stroke={CHART_GRID_BORDER_COLOR}
          strokeWidth={1}
          position="end"
        />

        <XAxis
          dataKey="xValue"
          tickLine={false}
          axisLine={{ stroke: CHART_GRID_BORDER_COLOR }}
          tick={{ fill: CHART_X_AXIS_TICK_LABEL_COLOR, fontSize: 12 }}
          interval={0}
          tickFormatter={(value, index) => {
            return index === data.length - 1 ? 'Current' : value
          }}
        />

        <YAxis
          width={20}
          tickLine={false}
          axisLine={{ stroke: CHART_GRID_BORDER_COLOR }}
          tick={{ fill: CHART_Y_AXIS_TICK_LABEL_COLOR, fontSize: 10 }}
          tickFormatter={(value, index) => {
            const display = config.type === 'storyCycleTime' ? Math.round(value / (3600 * 1000)).toString() : value
            return index === 0 || index === data.length - 1 ? display : ''
          }}
        />

        {config.bars.map((config) => (
          <Bar
            key={config.key}
            dataKey={config.key}
            name={config.name}
            stackId="a"
            fill={config.color}
            isAnimationActive={false}
            // @ts-ignore
            activeBar={(props) => {
              return (
                <Rectangle
                  {...props}
                  fill={config.activeColor}
                  stroke="none" // no gap between bars
                />
              )
            }}
          />
        ))}

        <Tooltip
          content={config.tooltip && <ChartTooltip {...config.tooltip} />}
          cursor={<CustomCursor />}
          position={{ y: height - 10 }}
          wrapperStyle={{ zIndex: 9999 }}
        />
      </RechartsBarChart>
    </ResponsiveContainer>
  )
}
